import { InteractiveSurface } from '@viquard/doc-ui';
import { useRef, type ReactNode, type PointerEvent as ReactPointerEvent } from 'react';
import type { CalendarEvent, CalendarKindMap } from '../types.js';
import { resolveKind } from '../utils/kindTheme.js';
import type { PositionedEvent } from '../utils/layout.js';
import { useEventDrag, type EventDragCommit } from '../hooks/useEventDrag.js';
import { useEventContextMenu } from '../hooks/useEventContextMenu.js';
import type { EventMenuActions } from '../utils/eventMenu.js';
import { useCalendarMessages } from '../i18n.js';
import { formatEventLocation } from '../utils/location.js';

export interface EventTimeBlockProps {
  readonly positioned: PositionedEvent;
  readonly kindTheme: CalendarKindMap;
  readonly startHour: number;
  readonly hourHeight: number;
  readonly timeLabel?: string | null | undefined;
  readonly showKindLabel?: boolean | undefined;
  readonly inset?: number | undefined;
  readonly density?: 'comfortable' | 'dense' | undefined;
  readonly onClick?: ((event: CalendarEvent) => void) | undefined;
  /** Day key the block lives in — required when drag-to-reschedule is
   *  enabled, so cross-column moves can resolve a target day. */
  readonly dayKey?: string | undefined;
  /** When provided, the block becomes draggable: pointer-down + drag
   *  fires `onMoveCommit({ minutesDelta, targetDayKey })`. */
  readonly onMoveCommit?: ((commit: EventDragCommit) => void) | undefined;
  /** When provided, a bottom-edge resize handle is rendered. Drag fires
   *  `onResizeCommit({ minutesDelta })`. */
  readonly onResizeCommit?: ((commit: EventDragCommit) => void) | undefined;
  /** Right-click context menu actions. */
  readonly contextMenu?: EventMenuActions | undefined;
  readonly selected?: boolean | undefined;
  /** Snap interval for both drag flavours. Default 15 min. */
  readonly snapMinutes?: number | undefined;
}

/**
 * A timed-grid event block with overlap-aware sizing + optional drag-
 * to-reschedule + drag-to-resize + right-click context menu.
 *
 * Width / left are computed from the positioner's column / columns so
 * two events at the same time render side-by-side. Height is driven by
 * the event's actual duration (clamped to a 30-minute floor).
 */
export function EventTimeBlock(props: EventTimeBlockProps): ReactNode {
  const {
    positioned,
    kindTheme,
    startHour,
    hourHeight,
    timeLabel,
    showKindLabel = false,
    inset = 4,
    density = 'comfortable',
    onClick,
    dayKey,
    onMoveCommit,
    onResizeCommit,
    contextMenu,
    selected = false,
    snapMinutes = 15,
  } = props;
  const messages = useCalendarMessages();
  const { event, startHour: sh, endHour: eh, column, columns } = positioned;
  const kind = resolveKind(kindTheme, event.kind);
  const locationLabel = formatEventLocation(event, messages);

  const moveDrag = useEventDrag({
    hourHeight,
    snapMinutes,
    originDayKey: dayKey ?? '',
    mode: 'move',
    onCommit: (commit) => onMoveCommit?.(commit),
    disabled: onMoveCommit === undefined || dayKey === undefined,
  });
  const resizeDrag = useEventDrag({
    hourHeight,
    snapMinutes,
    originDayKey: dayKey ?? '',
    mode: 'resize',
    onCommit: (commit) => onResizeCommit?.(commit),
    disabled: onResizeCommit === undefined,
  });

  const top = (sh - startHour) * hourHeight;
  const baseHeight = Math.max(20, (eh - sh) * hourHeight - 2);
  const widthPct = 100 / columns;
  const leftPct = column * widthPct;

  const cancelled = event.status === 'cancelled';
  const dragging = moveDrag.visual.active || resizeDrag.visual.active;

  const wrapperClass = [
    'vq-cal__timeblock',
    density === 'dense' ? 'is-dense' : '',
    cancelled ? 'is-cancelled' : '',
    dragging ? 'is-dragging' : '',
    selected ? 'is-selected' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const renderedHeight = baseHeight + resizeDrag.visual.extraHeight;

  const style = {
    top,
    height: Math.max(20, renderedHeight),
    left: `calc(${leftPct}% + ${inset}px)`,
    width: `calc(${widthPct}% - ${inset * 2}px)`,
    transform:
      moveDrag.visual.active && !resizeDrag.visual.active
        ? `translate(${moveDrag.visual.translateX}px, ${moveDrag.visual.translateY}px)`
        : undefined,
    ['--vq-cal-kind' as string]: kind.color,
  } as const;

  const blockRef = useRef<HTMLDivElement>(null);

  const inner = (
    <>
      <div className="vq-cal__timeblock-eyebrow">
        {timeLabel !== undefined && timeLabel !== null && (
          <span className="vq-cal__timeblock-time">{timeLabel}</span>
        )}
        {showKindLabel && <span className="vq-cal__timeblock-kind">{kind.label}</span>}
      </div>
      <div className="vq-cal__timeblock-title">{event.title}</div>
      {(locationLabel !== null || event.meta !== undefined) && (
        <div className="vq-cal__timeblock-meta">
          {[locationLabel, event.meta].filter((value): value is string => value !== null && value !== undefined).join(' · ')}
        </div>
      )}
      {onResizeCommit !== undefined && (
        <span
          className="vq-cal__timeblock-resize"
          onPointerDown={(e) => {
            e.stopPropagation();
            resizeDrag.bind.onPointerDown(e);
          }}
          aria-hidden="true"
        />
      )}
    </>
  );

  // We use a non-button wrapper so child elements (resize handle) can
  // be interactive without nesting buttons. Click is a synthetic onClick.
  const handleClick = (): void => {
    // If a real drag just happened, suppress the click that would
    // otherwise re-open the event.
    if (moveDrag.suppressNextClick() || resizeDrag.suppressNextClick()) return;
    onClick?.(event);
  };

  const handlePointerDown = (e: ReactPointerEvent<HTMLElement>): void => {
    // Stop propagation so this pointerdown doesn't also start a
    // drag-to-create gesture on the underlying column.
    e.stopPropagation();
    if (onMoveCommit !== undefined && dayKey !== undefined) {
      moveDrag.bind.onPointerDown(e);
    }
  };

  const cm = useEventContextMenu(event, contextMenu ?? {}, {
    disabled: contextMenu === undefined,
    ariaLabel: messages.eventActionsAriaLabel,
  });

  return (
    <>
      <InteractiveSurface
        ref={blockRef}
        role={onClick !== undefined ? 'button' : 'group'}
        tabIndex={onClick !== undefined ? 0 : -1}
        className={wrapperClass}
        style={style}
        title={event.title}
        onClick={onClick !== undefined ? handleClick : undefined}
        onPointerDown={handlePointerDown}
        onContextMenu={cm.bind.onContextMenu}
        aria-pressed={onClick !== undefined ? selected : undefined}
        data-event-id={event.id}
      >
        {inner}
      </InteractiveSurface>
      {cm.menu}
    </>
  );
}
